import { useEffect, useState } from "react";
import { BRAND_LOGO } from "../../lib/brand";
import { DeviceMobile, X, ShareNetwork, Plus } from "@phosphor-icons/react";

const STORAGE_KEY = "tsc_pwa_prompt_state";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true ||
    document.referrer.startsWith("android-app://")
  );
}

function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function isSafari() {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes("safari") && !ua.includes("chrome") && !ua.includes("crios") && !ua.includes("fxios");
}

/**
 * PWAInstallPrompt — luxury install banner.
 *
 * Behavior:
 * - Hidden if already installed (standalone display mode).
 * - On Chrome/Edge/Android: captures `beforeinstallprompt`, shows custom UI; clicking
 *   "Install" triggers native prompt.
 * - On iOS Safari: shows instructional banner (tap share → add to home screen).
 * - Dismissals persist in localStorage; we re-offer after 7 days.
 */
export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed

    // Throttle: respect previous dismissal for 7 days
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const { dismissedAt } = JSON.parse(raw);
        if (dismissedAt && Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
      }
    } catch {}

    // Chromium/Android path
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Small delay so it doesn't slam in instantly on first load
      setTimeout(() => setVisible(true), 1200);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ installedAt: Date.now() })); } catch {}
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS path — no beforeinstallprompt; show instructional banner after a short delay
    if (isIOS() && isSafari()) {
      setIosMode(true);
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onPrompt);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() })); } catch {}
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      } else {
        dismiss();
      }
    } catch {
      dismiss();
    } finally {
      setDeferred(null);
    }
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-x-3 bottom-4 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[420px] z-[80] rise-in" data-testid="pwa-install-prompt">
      <div className="relative bg-[#0a0a0a] border border-[#D4AF37]/40 rounded-md shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)] overflow-hidden grain">
        <button onClick={dismiss} className="absolute top-2.5 right-2.5 text-white/50 hover:text-white p-1" data-testid="pwa-prompt-close" aria-label="Dismiss">
          <X size={16} />
        </button>
        <div className="p-5 sm:p-6 flex gap-4">
          <img src={BRAND_LOGO} alt="" className="h-12 w-12 rounded-full ring-1 ring-[#D4AF37]/40 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Inside the Circle</p>
            <p className="font-display text-xl text-white mt-1 leading-tight">Download the App</p>
            {iosMode ? (
              <>
                <p className="text-white/65 text-xs mt-2 leading-relaxed">
                  Install The Social Circle for a faster, full-screen experience.
                </p>
                <p className="text-white/55 text-xs mt-2 leading-relaxed flex items-center gap-1.5 flex-wrap">
                  <span>Tap</span><ShareNetwork size={14} className="text-[#D4AF37]" />
                  <span>then</span>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-[#D4AF37]/40 text-[#D4AF37]"><Plus size={10} /> Add to Home Screen</span>
                </p>
                <button onClick={dismiss} className="tsc-btn-outline mt-4" data-testid="pwa-prompt-ios-dismiss">Got it</button>
              </>
            ) : (
              <>
                <p className="text-white/65 text-xs mt-2 leading-relaxed">
                  Add The Social Circle to your home screen — for instant access to your member QR, partner venues, and the calendar.
                </p>
                <div className="mt-4 flex items-center gap-3">
                  <button onClick={install} className="tsc-btn-gold inline-flex items-center gap-2" data-testid="pwa-prompt-install">
                    <DeviceMobile size={14} /> Install
                  </button>
                  <button onClick={dismiss} className="text-white/55 text-[10px] tracking-luxe uppercase hover:text-white" data-testid="pwa-prompt-later">Not now</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
